import qs from "qs";

// Componentss
import {
    getProjectSearchAiBrief,
    getSearchAiCompaniesLonglist,
    getSearchAiLeadersLongList,
    getSearchAiLeadersShortList,
} from "pages/ProjectDashboard";

// Utils
import { MenuItem } from "./mainMenu.config";
import { buildParamsString, isBriefCompleted } from "utils";

// Types
import { ProjectChunks } from "services/projects/project.types";
import { TPaceAssessmentReportStatus } from "store/slice/Pace";
import { urlPaths } from "enums/urlPaths";
import { ISessionResponse } from "store/slice/auth/authSlice.types";
import { flattenDeep } from "lodash";

export type MainMenuDataForItem = ProjectChunks & {
    sessionData: ISessionResponse | null;
    reportStatus: TPaceAssessmentReportStatus;
};

type TQueryParams = { [key: string]: string | undefined };

/**
 * Check if a term is in the pathbase (the part of the url before the "?" if there's one)
 *
 * @param {string} pathBase
 * @param {string[]} terms
 * @returns {boolean}
 */
const isTermInPathBase = (pathBase: string | undefined, terms: string[] | undefined) => {
    if (!pathBase || !terms?.length) {
        return false;
    }

    const result = terms.find((term) => pathBase.includes(term));

    return Boolean(result);
};

/**
 * Update PACE Item state
 *
 * @param {MenuItem} item
 * @param {string} pathname
 * @param {MainMenuDataForItem} dataChunks
 * @returns {MenuItem} Updated item with selected and disabled props
 */
export const updatePaceItems = (item: MenuItem, pathname: string, dataChunks: MainMenuDataForItem): MenuItem => {
    const [pathBase] = pathname.split("?");

    const reportStatus = dataChunks.reportStatus;
    const personId = dataChunks.sessionData?.authn.person?.id;

    const lastAssessment = reportStatus?.user_assessments?.find((assessment) => assessment.meta.status === "ACTIVE");
    const completedAssessment = reportStatus?.user_assessments?.find(
        (assessment) => assessment.meta.status !== "ACTIVE"
    );
    const isFirstAssessment = Boolean(
        reportStatus?.count_completed === 0 ||
            (reportStatus?.user_assessments?.length === 1 &&
                reportStatus?.user_assessments[0]?.meta?.status === "ACTIVE")
    );

    const userId = reportStatus?.user_id;
    const assessmentId = completedAssessment?.user_assessment_id;
    const reportUrl = `/pace/report/user/${userId}/assessment/${assessmentId}`;

    if ((item.id === "my-dashboard" || item.id === "my-dashboard-1") && userId && assessmentId) {
        const currentExtraPaths = item.extraPaths || [];
        const hasreportUrl = item.extraPaths?.includes(reportUrl);
        const newExtraPaths = hasreportUrl ? currentExtraPaths : [...currentExtraPaths, item?.url || "", reportUrl];

        return {
            ...item,
            isSelected: isTermInPathBase(pathBase, newExtraPaths),
            extraPaths: newExtraPaths,
        };
    }

    if (item.id === "pace-assessment") {
        const activeAssessmentId = lastAssessment?.user_assessment_id;
        const assessmentUrl = `${urlPaths.PaceLanding}/${activeAssessmentId}`.replace("/undefined", "");

        return {
            ...item,
            url: isFirstAssessment ? assessmentUrl : reportUrl,
            isSelected: pathname.includes(item?.url || "") || pathBase === assessmentUrl,
            disabled: !Boolean(userId),
        };
    }

    if (item.id === "my-pace") {
        return {
            ...item,
            isSelected: pathname.includes(`${item.base}${item.path}`),
            disabled: !Boolean(userId && assessmentId),
        };
    }

    if (item.id === "my-experience") {
        return {
            ...item,
            isSelected: isTermInPathBase(pathBase, item?.extraPaths) || pathname.includes(`${item.base}${item.path}`),
            disabled: !Boolean(personId),
        };
    }

    const children = item.children?.map((group) => {
        return group.map((child) => {
            const isCurrentPath = pathname.includes(`${child.base}${child.path}`);

            const item = {
                ...child,
                isSelected: isCurrentPath,
                disabled: !Boolean(completedAssessment),
            };

            return item;
        });
    });

    const isUrlRelated = isTermInPathBase(pathBase, item?.extraPaths) || pathBase === item.url;

    return {
        ...item,
        children,
        disabled: false,
        isSelected: isUrlRelated,
        isExpanded: isUrlRelated,
    };
};

/**
 * Update My Searches Item state
 *
 * @param {MenuItem} item
 * @param {string} pathname
 * @returns {MenuItem} Updated item with selected and disabled props
 */
export const updateLDMySearchesItem = (item: MenuItem, pathname: string): MenuItem => {
    if (
        !pathname.includes("leadership-dynamics") ||
        pathname.includes("projectId") ||
        pathname.includes("briefId") ||
        pathname.includes("searchAiId")
    ) {
        return {
            ...item,
            isSelected: false,
        };
    }

    return { ...item, isSelected: true };
};

/**
 * Update Dashboard Item state
 *
 * @param {MenuItem} item
 * @param {string} pathname
 * @returns {MenuItem} Updated item with selected and disabled props
 */
export const updateLDOrgDashboardItem = (item: MenuItem, pathname: string) => {
    const [, pathQueryParams] = pathname.split("?");

    const { projectId: projectIdFromQuery } = qs.parse(pathQueryParams, {
        ignoreQueryPrefix: true,
    }) as TQueryParams;

    const projectId =
        projectIdFromQuery || (pathname.includes("leadership-dynamics") ? pathname.split("/").at(-1) : undefined);

    if (!projectId || !Number(projectId)) {
        return {
            ...item,
            isSelected: false,
            disabled: true,
        };
    }

    return { ...item, url: `${item.base}${item.path}/${projectId}`, isSelected: true, disabled: false };
};

/**
 * Update Leadership Dashboard items state
 *
 * @param {MenuItem} item
 * @param {string} pathname
 * @param {MainMenuDataForItem} dataChunks
 * @returns {MenuItem} Updated item with selected and disabled props
 */
export const updateLDDashboardItem = (item: MenuItem, pathname: string, dataChunks: MainMenuDataForItem) => {
    if (!pathname.includes("insightId")) {
        return {
            ...item,
            isSelected: false,
            isExpanded: false,
            disabled: true,
        };
    }

    const [pathBase, pathQueryParams] = pathname.split("?");
    const { projectId, insightId, briefId, searchAiId } = qs.parse(pathQueryParams, {
        ignoreQueryPrefix: true,
    }) as TQueryParams;

    const companyIdFromPath = /\/\d+/gi.exec(pathBase)?.[0].substring(1);
    const companyId = dataChunks?.data.id || companyIdFromPath;

    const children = item.children?.map((group, index) => {
        // If index === 0 then it's Overview, Management & Board Assessment and Path to improvement submenu
        if (index === 0) {
            return group.map((child) => {
                const queryParams = buildParamsString({ projectId, insightId, briefId, searchAiId });
                const url = child.id.includes("path-to-improvement")
                    ? `${child.base}${child.path}/${companyId}/bridge?${queryParams}`
                    : `${child.base}${child.path}/${companyId}?${queryParams}`;

                const isCurrentPath = pathname === url;

                const item = {
                    ...child,
                    url,
                    isSelected: isCurrentPath,
                    disabled: !Boolean(projectId && insightId),
                };

                return item;
            });
        }

        // If index === 1 then it's Insights
        if (index === 1) {
            const insightsItem = group[0];
            const insightsChildren = insightsItem?.children?.map((children) => {
                // Inside Insights we have the charts
                return children.map((child) => {
                    const queryParams = buildParamsString({ projectId, insightId, briefId, searchAiId });
                    const url = `${child.base}${child.path}/${companyId}/${child.id}?${queryParams}`;
                    const isCurrentPath = pathname === url;

                    return {
                        ...child,
                        url,
                        isSelected: isCurrentPath,
                        disabled: !Boolean(companyId && projectId && insightId),
                    };
                });
            });

            const allChildrenUrl = insightsChildren?.[0].map((child) => child?.url);
            const isUrlRelated =
                Boolean(allChildrenUrl?.find((url) => url === pathname)) ||
                isTermInPathBase(pathBase, item?.extraPaths);

            return [
                {
                    ...insightsItem,
                    children: insightsChildren,
                    disabled: !Boolean(companyId && projectId && insightId),
                    isSelected: Boolean(companyId && projectId && insightId) && isUrlRelated,
                    isExpanded: Boolean(companyId && projectId && insightId) && isUrlRelated,
                },
            ];
        }

        return group;
    });

    const allChildrenUrl = flattenDeep(children).map((child) => child?.url);
    const isUrlRelated =
        Boolean(allChildrenUrl?.find((url) => url === pathname)) || isTermInPathBase(pathBase, item?.extraPaths);

    return {
        ...item,
        children,
        disabled: false,
        isSelected: isUrlRelated,
        isExpanded: isUrlRelated,
    };
};

/**
 * Update Brief items state
 *
 * @param {MenuItem} item
 * @param {string} pathname
 * @returns {MenuItem} Updated item with selected and disabled props
 */
export const updateLDBriefItem = (item: MenuItem, pathname: string, dataChunks: MainMenuDataForItem) => {
    const [pathBase, pathQueryParams] = pathname.split("?");

    const { projectId, insightId, briefId, searchAiId } = qs.parse(pathQueryParams, {
        ignoreQueryPrefix: true,
    }) as TQueryParams;

    const brief = dataChunks.briefs.find((brief) => brief.data.id === Number(briefId));
    const isEditing = isBriefCompleted(brief);

    const companyIdFromPath = /\/\d+/gi.exec(pathBase)?.[0].substring(1);
    const companyId = dataChunks?.data.id || companyIdFromPath;
    const briefUrl = isEditing ? `/review/${companyId}` : `/${companyId}`;

    const queryParams = buildParamsString({ projectId, insightId, briefId, searchAiId });

    if (pathBase.includes("brief")) {
        return {
            ...item,
            url: `${item.base}${item.path}${briefUrl}?${queryParams}`,
            isSelected: true,
            disabled: !Boolean(briefId),
        };
    }

    return {
        ...item,
        url: briefId ? `${item.base}${item.path}${briefUrl}?${queryParams}` : urlPaths.Brief,
        isSelected: false,
        disabled: !Boolean(briefId),
    };
};

/**
 * Update Search AI items state
 *
 * @param {MenuItem} item
 * @param {string} pathname
 * @param {MainMenuDataForItem} dataChunks
 * @returns {MenuItem} Updated item with selected and disabled props
 */
export const updateLDSearchAiItem = (item: MenuItem, pathname: string, dataChunks: MainMenuDataForItem) => {
    if (!pathname.includes("searchAiId")) {
        return {
            ...item,
            isSelected: false,
            isExpanded: false,
            disabled: true,
        };
    }

    const [pathBase, pathQueryParams] = pathname.split("?");
    const { projectId, insightId, briefId, searchAiId } = qs.parse(pathQueryParams, {
        ignoreQueryPrefix: true,
    }) as TQueryParams;

    const companyIdFromPath = /\/\d+/gi.exec(pathBase)?.[0].substring(1);
    const companyId = dataChunks?.data.id || companyIdFromPath;

    const searchAi = dataChunks.searchAIs.find((searchAi) => searchAi.data.id === Number(searchAiId));

    // Check which parts of the Search AI are completed (Companies and Leaderss)
    const relatedBriefPath = getProjectSearchAiBrief(searchAi)?.ref?.path;
    const relatedCompaniesPath = getSearchAiCompaniesLonglist(searchAi)?.ref?.path;
    const relatedLongListPath = getSearchAiLeadersLongList(searchAi)?.ref?.path;
    const relatedShortListPath = getSearchAiLeadersShortList(searchAi)?.ref?.path;

    const hasShortListOrLongList = Boolean(relatedLongListPath || relatedShortListPath);
    const isUrlRelated = isTermInPathBase(pathBase, item?.extraPaths);

    const children = item.children?.map((group) => {
        return group.map((child) => {
            const queryParams = buildParamsString({ projectId, insightId, briefId, searchAiId });

            const isCurrentPath = pathname.includes(`${child.base}${child.path}/${companyId}`);
            const url = `${child.base}${child.path}/${companyId}?${queryParams}`;

            const item = {
                ...child,
                url,
                isSelected: isCurrentPath,
                disabled: false,
            };

            if (child.id === "company-search") {
                return { ...item, disabled: !relatedBriefPath };
            }

            if (child.id === "people-search") {
                return { ...item, disabled: !relatedCompaniesPath };
            }

            if (child.id === "refinement") {
                return { ...item, disabled: !hasShortListOrLongList };
            }

            return item;
        });
    });

    return {
        ...item,
        children,
        disabled: !Boolean(searchAi),
        isSelected: isUrlRelated,
        isExpanded: isUrlRelated,
    };
};
